import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { 
  Megaphone, Plus, Eye, Edit, Trash2, TrendingUp, Wallet, CreditCard, 
  ArrowRight, Loader2, AlertCircle, CheckCircle, Sparkles, Calendar,
  RefreshCcw, Settings, Bell, Star, X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import UniversalPaymentCheckout from '../../components/UniversalPaymentCheckout';

const VendorAdvertising = () => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('100'); // Will be updated based on plan
  const [adBalance, setAdBalance] = useState(0);
  const [stats, setStats] = useState({
    active_campaigns: 0,
    total_spend: 0,
    impressions: 0,
    clicks: 0
  });
  
  // Plan info state
  const [planInfo, setPlanInfo] = useState({
    plan_code: 'basic',
    plan_name: 'Basic',
    monthly_ad_credits: 50,
    default_topup_amount: 50
  });
  
  // Universal Payment Checkout state
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);
  const [pendingTopUpAmount, setPendingTopUpAmount] = useState(0);
  
  // Auto top-up settings state
  const [autoTopUpSettings, setAutoTopUpSettings] = useState({
    enabled: false,
    amount: 100,
    threshold: 50,
    max_monthly_topups: 5,
    payment_method_id: null
  });
  const [showAutoTopUpSettings, setShowAutoTopUpSettings] = useState(false);
  const [savingAutoTopUp, setSavingAutoTopUp] = useState(false);
  
  // Saved payment methods state
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Fetch saved payment methods
  const fetchPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try {
      const result = await apiCall('/api/razorpay/payment-methods');
      setSavedPaymentMethods(result?.payment_methods || []);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      setSavedPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, [apiCall]);

  // Delete payment method
  const deletePaymentMethod = async (methodId) => {
    try {
      await apiCall(`/api/razorpay/payment-methods/${methodId}`, {
        method: 'DELETE'
      });
      toast({
        title: 'Card Removed',
        description: 'Payment method has been removed.',
      });
      fetchPaymentMethods();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove payment method',
        variant: 'destructive'
      });
    }
  };

  // Set default payment method
  const setDefaultPaymentMethod = async (methodId) => {
    try {
      await apiCall(`/api/razorpay/payment-methods/${methodId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_default: true })
      });
      toast({
        title: 'Default Card Updated',
        description: 'This card will be used for auto top-ups.',
      });
      fetchPaymentMethods();
      // Update auto top-up settings with this card
      setAutoTopUpSettings(prev => ({...prev, payment_method_id: methodId}));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update default card',
        variant: 'destructive'
      });
    }
  };

  // Fetch auto top-up settings
  const fetchAutoTopUpSettings = useCallback(async () => {
    try {
      const settings = await apiCall('/api/ad-wallet/auto-topup/settings');
      setAutoTopUpSettings(settings);
    } catch (error) {
      console.error('Failed to fetch auto top-up settings:', error);
    }
  }, [apiCall]);

  // Save auto top-up settings
  const saveAutoTopUpSettings = async () => {
    // Check if payment method is selected when enabling
    if (autoTopUpSettings.enabled && savedPaymentMethods.length === 0) {
      toast({
        title: 'No Payment Method',
        description: 'Please add a payment method first by making a manual top-up.',
        variant: 'destructive'
      });
      return;
    }
    
    setSavingAutoTopUp(true);
    try {
      await apiCall('/api/ad-wallet/auto-topup/settings', {
        method: 'POST',
        body: JSON.stringify(autoTopUpSettings)
      });
      toast({
        title: 'Settings Saved',
        description: autoTopUpSettings.enabled 
          ? `Auto top-up enabled. ₹${autoTopUpSettings.amount} will be added when balance falls below ₹${autoTopUpSettings.threshold}.`
          : 'Auto top-up has been disabled.',
      });
      setShowAutoTopUpSettings(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save auto top-up settings',
        variant: 'destructive'
      });
    } finally {
      setSavingAutoTopUp(false);
    }
  };

  // Fetch vendor's plan info for ad credits
  const fetchPlanInfo = useCallback(async () => {
    try {
      const info = await apiCall('/api/ad-wallet/vendor-plan-info');
      setPlanInfo(info);
      // Set default top-up amount based on plan
      setTopUpAmount(String(info.default_topup_amount || 100));
    } catch (error) {
      console.error('Failed to fetch plan info:', error);
      // Fallback to Basic plan defaults
      setPlanInfo({
        plan_code: 'basic',
        plan_name: 'Basic',
        monthly_ad_credits: 50,
        default_topup_amount: 50
      });
      setTopUpAmount('50');
    }
  }, [apiCall]);

  // Fetch wallet balance from API
  const fetchWalletBalance = useCallback(async () => {
    try {
      const balanceData = await apiCall('/api/ad-wallet/balance');
      setAdBalance(balanceData?.available_balance || 0);
    } catch (error) {
      console.error('Failed to fetch wallet balance:', error);
      setAdBalance(0);
    }
  }, [apiCall]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch campaigns, wallet balance, plan info, auto top-up settings, and payment methods in parallel
      const [campaignsResult] = await Promise.all([
        apiCall('/api/vendor/campaigns').catch(() => []),
        fetchWalletBalance(),
        fetchPlanInfo(),
        fetchAutoTopUpSettings(),
        fetchPaymentMethods()
      ]);
      
      setCampaigns(campaignsResult || []);
      
      // Calculate stats from campaigns
      const activeCampaigns = (campaignsResult || []).filter(c => c.status === 'active');
      setStats({
        active_campaigns: activeCampaigns.length,
        total_spend: (campaignsResult || []).reduce((sum, c) => sum + (c.spend || 0), 0),
        impressions: (campaignsResult || []).reduce((sum, c) => sum + (c.impressions || 0), 0),
        clicks: (campaignsResult || []).reduce((sum, c) => sum + (c.clicks || 0), 0)
      });
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, fetchWalletBalance, fetchPlanInfo, fetchAutoTopUpSettings, fetchPaymentMethods]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Handle top-up button click - validate and open payment checkout
  const handleTopUpClick = () => {
    const amount = parseFloat(topUpAmount);
    const minAmount = Math.min(planInfo.default_topup_amount, 50); // Minimum ₹50 or plan default
    
    if (!amount || amount < minAmount) {
      toast({
        title: 'Invalid Amount',
        description: `Minimum top-up amount is ₹${minAmount}`,
        variant: 'destructive'
      });
      return;
    }
    
    // Store the amount and open payment checkout
    setPendingTopUpAmount(amount);
    setShowTopUp(false);
    setShowPaymentCheckout(true);
  };

  // Handle successful payment from Universal Checkout
  const handlePaymentSuccess = (result) => {
    const creditsAdded = result.credits_added || pendingTopUpAmount;
    
    toast({
      title: 'Credits Added Successfully',
      description: `₹${creditsAdded.toLocaleString()} has been added to your ad wallet.${result.gst_voucher_number ? ` GST Voucher: ${result.gst_voucher_number}` : ''}`,
    });
    
    // Update balance
    setAdBalance(prev => prev + creditsAdded);
    
    // Reset states
    setShowPaymentCheckout(false);
    setTopUpAmount(String(planInfo.default_topup_amount || 100));
    setPendingTopUpAmount(0);
    
    // Refresh wallet balance from API
    fetchWalletBalance();
  };

  // Handle payment failure
  const handlePaymentError = (error) => {
    toast({
      title: 'Payment Failed',
      description: error.description || error.message || 'Payment was not successful. Credits have not been added.',
      variant: 'destructive'
    });
    
    // Reset states but keep the dialog open for retry
    setShowPaymentCheckout(false);
    setPendingTopUpAmount(0);
  };

  // Handle payment modal close without completing
  const handlePaymentClose = () => {
    setShowPaymentCheckout(false);
    setPendingTopUpAmount(0);
    toast({
      title: 'Payment Cancelled',
      description: 'Credit top-up was cancelled. No amount has been charged.',
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Active', class: 'bg-green-100 text-green-800' },
      paused: { label: 'Paused', class: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Completed', class: 'bg-gray-100 text-gray-800' },
      draft: { label: 'Draft', class: 'bg-blue-100 text-blue-800' }
    };
    const cfg = config[status] || config.draft;
    return <Badge className={cfg.class}>{cfg.label}</Badge>;
  };

  // Calculate breakdown for display
  const calculateTopUp = () => {
    const amount = parseFloat(topUpAmount) || 0;
    const gst = amount * 0.18;
    const gross = amount + gst;
    return { amount, gst, gross };
  };

  const calc = calculateTopUp();

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
              <Megaphone className="h-6 w-6 md:h-8 md:w-8" />
              Advertising
            </h1>
            <p className="text-gray-500 mt-1">Create and manage your advertising campaigns</p>
          </div>
          <Button onClick={() => window.location.href = '/create-campaign'}>
            <Plus className="h-4 w-4 mr-2" />
            Create Campaign
          </Button>
        </div>

        {/* Ad Credit Balance - Prominent Card */}
        <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Wallet className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Ad Credit Balance</p>
                  <p className="text-4xl font-bold" data-testid="ad-balance">₹{adBalance.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {showTopUp ? (
                  <div className="flex flex-col gap-3 bg-white/10 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder={`Amount (min ₹${Math.min(planInfo.default_topup_amount, 50)})`}
                        value={topUpAmount}
                        onChange={(e) => setTopUpAmount(e.target.value)}
                        className="w-40 bg-white text-gray-900"
                        min={Math.min(planInfo.default_topup_amount, 50)}
                        data-testid="topup-amount-input"
                      />
                      <Button 
                        variant="secondary" 
                        onClick={handleTopUpClick}
                        data-testid="topup-proceed-btn"
                      >
                        Proceed to Pay
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="text-white hover:bg-white/20" 
                        onClick={() => {
                          setShowTopUp(false);
                          setTopUpAmount(String(planInfo.default_topup_amount || 100));
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    {/* Amount breakdown */}
                    {calc.amount >= 50 && (
                      <div className="text-sm text-white/90 space-y-1">
                        <div className="flex justify-between">
                          <span>Base Amount:</span>
                          <span>₹{calc.amount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>GST (18%):</span>
                          <span>₹{calc.gst.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t border-white/30 pt-1">
                          <span>Total Payable:</span>
                          <span>₹{calc.gross.toFixed(2)}</span>
                        </div>
                        <div className="text-white/70 text-xs">
                          Credits Added: ₹{calc.amount.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Button 
                    variant="secondary" 
                    className="gap-2" 
                    onClick={() => setShowTopUp(true)}
                    data-testid="topup-credits-btn"
                  >
                    <CreditCard className="h-4 w-4" />
                    Top Up Credits
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap items-center gap-4 md:gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-yellow-300" />
                <span className="text-white/70">Monthly Allocation ({planInfo.plan_name}):</span>
                <span className="font-semibold">₹{planInfo.monthly_ad_credits}</span>
              </div>
              <div>
                <span className="text-white/70">This Month Spent:</span>
                <span className="ml-2 font-semibold">₹{stats.total_spend.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-white/70">Active Campaigns:</span>
                <span className="ml-2 font-semibold">{stats.active_campaigns}</span>
              </div>
            </div>
            {/* Plan info note */}
            <div className="mt-3 text-xs text-white/60 flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              <span>Credits roll over monthly. Unused credits are preserved.</span>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Active Campaigns</p>
                <p className="text-3xl font-bold text-blue-600">{stats.active_campaigns}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Spend</p>
                <p className="text-3xl font-bold text-green-600">₹{stats.total_spend.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Impressions</p>
                <p className="text-3xl font-bold text-purple-600">
                  {stats.impressions.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Clicks</p>
                <p className="text-3xl font-bold text-orange-600">
                  {stats.clicks.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Campaigns</CardTitle>
            <CardDescription>Manage your active and past advertising campaigns</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-400" />
                <p className="text-gray-500">Loading campaigns...</p>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-12">
                <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No campaigns yet</p>
                <p className="text-sm text-gray-400 mb-4">
                  Create your first advertising campaign to promote your products
                </p>
                <Button onClick={() => window.location.href = '/create-campaign'}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Campaign
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{campaign.name}</h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <p className="text-sm text-gray-600">{campaign.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {campaign.impressions} views
                        </span>
                        <span className="flex items-center gap-1">
                          <TrendingUp className="h-4 w-4" />
                          {campaign.clicks} clicks
                        </span>
                        <span>Budget: ₹{campaign.budget}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Text */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-900 mb-2">
              Boost Your Sales with Advertising
            </h3>
            <p className="text-sm text-blue-800">
              Create targeted campaigns to promote your products and reach more customers. 
              Track performance metrics and optimize your advertising spend for maximum ROI.
            </p>
          </CardContent>
        </Card>

        {/* Saved Payment Methods Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-indigo-600" />
                  Saved Payment Methods
                </CardTitle>
                <CardDescription>
                  Cards saved for quick top-ups and auto top-up
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPaymentMethods ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : savedPaymentMethods.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <CreditCard className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="mb-2">No saved payment methods</p>
                <p className="text-sm">Your card will be saved after your first top-up</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedPaymentMethods.map((method) => (
                  <div 
                    key={method.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      method.is_default ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'
                    }`}
                    data-testid={`payment-method-${method.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 bg-white rounded flex items-center justify-center border">
                        <span className="text-xs font-bold text-gray-700">
                          {method.card_network?.toUpperCase()?.slice(0, 4) || 'CARD'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">•••• {method.card_last4}</span>
                          <Badge variant="outline" className="text-xs">
                            {method.card_type}
                          </Badge>
                          {method.is_default && (
                            <Badge className="bg-indigo-600 text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{method.nickname}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!method.is_default && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setDefaultPaymentMethod(method.id)}
                          data-testid={`set-default-${method.id}`}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => deletePaymentMethod(method.id)}
                        data-testid={`delete-method-${method.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto Top-up Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCcw className="h-5 w-5 text-green-600" />
                  Auto Top-up Settings
                </CardTitle>
                <CardDescription>
                  Automatically add credits when your balance falls below a threshold
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAutoTopUpSettings(!showAutoTopUpSettings)}
                data-testid="auto-topup-toggle-btn"
              >
                <Settings className="h-4 w-4 mr-2" />
                {showAutoTopUpSettings ? 'Close' : 'Configure'}
              </Button>
            </div>
          </CardHeader>
          {showAutoTopUpSettings && (
            <CardContent className="border-t pt-6 space-y-6">
              {/* Warning if no payment methods */}
              {savedPaymentMethods.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">No Payment Method Saved</p>
                    <p className="text-sm text-yellow-700">
                      Make a manual top-up first to save a payment method for auto top-up.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-gray-600" />
                  <div>
                    <Label className="font-medium">Enable Auto Top-up</Label>
                    <p className="text-sm text-gray-500">
                      Automatically charge your saved payment method
                    </p>
                  </div>
                </div>
                <Switch
                  checked={autoTopUpSettings.enabled}
                  onCheckedChange={(checked) => 
                    setAutoTopUpSettings(prev => ({...prev, enabled: checked}))
                  }
                  data-testid="auto-topup-switch"
                />
              </div>

              {autoTopUpSettings.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Top-up Amount */}
                  <div className="space-y-2">
                    <Label>Top-up Amount (₹)</Label>
                    <Input
                      type="number"
                      value={autoTopUpSettings.amount}
                      onChange={(e) => 
                        setAutoTopUpSettings(prev => ({
                          ...prev, 
                          amount: Math.max(50, parseInt(e.target.value) || 50)
                        }))
                      }
                      min={50}
                      placeholder="₹100"
                      data-testid="auto-topup-amount"
                    />
                    <p className="text-xs text-gray-500">Min ₹50</p>
                  </div>

                  {/* Threshold */}
                  <div className="space-y-2">
                    <Label>Trigger Threshold (₹)</Label>
                    <Input
                      type="number"
                      value={autoTopUpSettings.threshold}
                      onChange={(e) => 
                        setAutoTopUpSettings(prev => ({
                          ...prev, 
                          threshold: Math.max(20, parseInt(e.target.value) || 20)
                        }))
                      }
                      min={20}
                      placeholder="₹50"
                      data-testid="auto-topup-threshold"
                    />
                    <p className="text-xs text-gray-500">Top-up when balance falls below this</p>
                  </div>

                  {/* Max Monthly Top-ups */}
                  <div className="space-y-2">
                    <Label>Max Top-ups/Month</Label>
                    <Input
                      type="number"
                      value={autoTopUpSettings.max_monthly_topups}
                      onChange={(e) => 
                        setAutoTopUpSettings(prev => ({
                          ...prev, 
                          max_monthly_topups: Math.min(10, Math.max(1, parseInt(e.target.value) || 1))
                        }))
                      }
                      min={1}
                      max={10}
                      placeholder="5"
                      data-testid="auto-topup-max"
                    />
                    <p className="text-xs text-gray-500">Limit: 1-10 per month</p>
                  </div>
                </div>
              )}

              {/* Summary & Save */}
              <div className="flex items-center justify-between pt-4 border-t">
                {autoTopUpSettings.enabled ? (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Summary:</span> Auto top-up ₹{autoTopUpSettings.amount} when balance falls below ₹{autoTopUpSettings.threshold} (max {autoTopUpSettings.max_monthly_topups}x/month)
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Auto top-up is currently disabled
                  </div>
                )}
                <Button 
                  onClick={saveAutoTopUpSettings}
                  disabled={savingAutoTopUp}
                  data-testid="save-auto-topup-btn"
                >
                  {savingAutoTopUp ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Universal Payment Checkout Modal */}
      <UniversalPaymentCheckout
        isOpen={showPaymentCheckout}
        onClose={handlePaymentClose}
        userContext={{
          name: user?.name,
          email: user?.email,
          mobile: user?.mobile || user?.phone,
          gstin: user?.gstin || user?.vendor_profile?.gstin,
          razorpayCustomerId: user?.razorpay_customer_id
        }}
        paymentContext={{
          type: 'AD_CREDIT',
          amount: pendingTopUpAmount,
          metaData: {
            source: 'advertising_page'
          }
        }}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />
    </DashboardLayout>
  );
};

export default VendorAdvertising;
